import { eckoDispatch } from '../utils/analytics';
const STORAGE_KEY = 'newsletter_translation_language';
const GOOGLE_COOKIE = 'googtrans';
const SCRIPT_ID = 'google-translate-element-script';
const CALLBACK_NAME = 'eckoGoogleTranslateElementInit';

function getStoredLanguage() {
	try {
		return window.localStorage.getItem(STORAGE_KEY) || '';
	} catch (e) {
		return '';
	}
}

function setStoredLanguage(language) {
	try {
		if (language) {
			window.localStorage.setItem(STORAGE_KEY, language);
		} else {
			window.localStorage.removeItem(STORAGE_KEY);
		}
	} catch (e) {
		// Ignore unavailable localStorage.
	}
}

function getCookie(name) {
	const prefix = `${name}=`;
	return document.cookie
		.split(';')
		.map(cookie => cookie.trim())
		.find(cookie => cookie.indexOf(prefix) === 0)
		?.slice(prefix.length) || '';
}

function clearGoogleTranslateCookies() {
	const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
	const hostParts = window.location.hostname.split('.');
	const domains = ['', window.location.hostname];

	if (hostParts.length > 1) {
		domains.push(`.${hostParts.slice(-2).join('.')}`);
	}

	domains.forEach(domain => {
		const domainPart = domain ? `; domain=${domain}` : '';
		document.cookie = `${GOOGLE_COOKIE}=; ${expires}; path=/${domainPart}`;
	});
}

function isTranslated() {
	return Boolean(getCookie(GOOGLE_COOKIE)) ||
		hasTranslatedClass();
}

function hasTranslatedClass() {
	return (
		document.documentElement.classList.contains('translated-ltr') ||
		document.documentElement.classList.contains('translated-rtl')
	);
}

function resetToDefault({ reload = true } = {}) {
	const wasTranslated = isTranslated();
	setStoredLanguage('');
	clearGoogleTranslateCookies();

	if (reload && wasTranslated) {
		window.location.reload();
	}
}

function parseConfig(widget) {
	try {
		const config = JSON.parse(widget.getAttribute('data-translation-config') || '{}');
		const allowedLanguages = Array.isArray(config.allowedLanguages)
			? config.allowedLanguages.map(String)
			: [];

		return {
			defaultLanguage: String(config.defaultLanguage || 'en'),
			allowedLanguages
		};
	} catch (e) {
		return {
			defaultLanguage: 'en',
			allowedLanguages: []
		};
	}
}

function waitForGoogleSelect(language) {
	return new Promise(resolve => {
		const existing = document.querySelector('.goog-te-combo');
		if (existing && existing.querySelector(`option[value="${language}"]`)) {
			resolve(existing);
			return;
		}

		let attempts = 0;
		const interval = window.setInterval(() => {
			attempts += 1;
			const select = document.querySelector('.goog-te-combo');
			const hasLanguage = select?.querySelector(`option[value="${language}"]`);

			if (hasLanguage || attempts >= 80) {
				window.clearInterval(interval);
				resolve(hasLanguage ? select : null);
			}
		}, 125);
	});
}

function loadGoogleTranslate(config) {
	return new Promise((resolve, reject) => {
		if (window.google?.translate?.TranslateElement) {
			resolve();
			return;
		}

		window[CALLBACK_NAME] = function () {
			new window.google.translate.TranslateElement(
				{
					pageLanguage: config.defaultLanguage,
					includedLanguages: config.allowedLanguages.join(','),
					autoDisplay: false,
					layout: window.google.translate.TranslateElement.InlineLayout.VERTICAL
				},
				'google_translate_element'
			);
			resolve();
		};

		if (document.getElementById(SCRIPT_ID)) {
			const waitForExistingScript = window.setInterval(() => {
				if (window.google?.translate?.TranslateElement) {
					window.clearInterval(waitForExistingScript);
					resolve();
				}
			}, 125);
			return;
		}

		const script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.src = `https://translate.google.com/translate_a/element.js?cb=${CALLBACK_NAME}`;
		script.async = true;
		script.onerror = reject;
		document.head.appendChild(script);
	});
}

function dispatchGoogleSelectChange(select) {
	select.dispatchEvent(new Event('input', { bubbles: true }));
	select.dispatchEvent(new Event('change', { bubbles: true }));

	if (document.createEvent) {
		const legacyEvent = document.createEvent('HTMLEvents');
		legacyEvent.initEvent('change', true, true);
		select.dispatchEvent(legacyEvent);
	}
}

async function applyLanguage(language, config) {
	if (!language || language === config.defaultLanguage) {
		resetToDefault();
		return;
	}

	try {
		await loadGoogleTranslate(config);
	} catch (error) {
		return;
	}

	const googleSelect = await waitForGoogleSelect(language);

	if (!googleSelect) {
		return;
	}

	googleSelect.value = language;
	dispatchGoogleSelectChange(googleSelect);
}

function clearStaleSelectionIfNeeded(widget, config, select) {
	const storedLanguage = getStoredLanguage();
	const allowed = new Set(config.allowedLanguages);

	if (!storedLanguage) {
		return '';
	}

	if (!allowed.has(storedLanguage) || storedLanguage === config.defaultLanguage) {
		select.value = config.defaultLanguage;
		resetToDefault({ reload: isTranslated() });
		return '';
	}

	return storedLanguage;
}

export function initEditionLanguage() {
	const isEditionPage = Boolean(document.querySelector('main.single .edition'));
	const widget = document.querySelector('[data-edition-language]');

	if (!widget) {
		if (isEditionPage && (getStoredLanguage() || isTranslated())) {
			resetToDefault({ reload: isTranslated() });
		}
		return;
	}

	const select = widget.querySelector('[data-edition-language-select]');
	if (!select) {
		resetToDefault({ reload: isTranslated() });
		return;
	}

	const config = parseConfig(widget);
	if (!config.allowedLanguages.includes(config.defaultLanguage)) {
		config.allowedLanguages.unshift(config.defaultLanguage);
	}

	const storedLanguage = clearStaleSelectionIfNeeded(widget, config, select);
	if (storedLanguage) {
		select.value = storedLanguage;
		applyLanguage(storedLanguage, config);
	} else {
		select.value = config.defaultLanguage;
	}

	select.addEventListener('change', () => {
		const language = select.value;

		eckoDispatch('ecko:languageSwitch', {
			language,
			defaultLanguage: config.defaultLanguage
		});

		if (!config.allowedLanguages.includes(language) || language === config.defaultLanguage) {
			select.value = config.defaultLanguage;
			resetToDefault();
			return;
		}

		setStoredLanguage(language);
		applyLanguage(language, config);
	});
}
